import { useUser } from '@app/context/UserContext';
import { NavigationSubsection, Topics } from '@app/context/constants';
import { subSectionUrl } from '@app/context/utils';
import { useNavigation } from '@app/hooks/useNavigation';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { getTopicStates } from '@app/utils/getTopicStates';
import { Icon } from '@components/ui';
import { Route } from 'react-router-dom';
import { LanguageDisclaimer } from '@components/ui/LanguageDisclaimer/LanguageDisclaimer';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import LockIcon from '@mui/icons-material/Lock';
import { Collapse, Tooltip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Switch, useRouteMatch } from 'react-router-dom';

import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { UserTopicApplicationsQuestionnaires } from '@dieterApi/user/useUserQuery';
import { Dashboard } from '../useDashboard';
import { ConsentForms } from './SubSections/ConsentForms/ConsentForms';
import { ConsentManagement } from './SubSections/ConsentManagement/ConsentManagement';
import { DSFAList } from './SubSections/DSFAList/DSFAList';
import { Incidents } from './SubSections/Incidents/Incidents';
import { InfoForms } from './SubSections/InfoForms/InfoForms';
import { PartnerListEntry } from './SubSections/PartnerListEntry/PartnerListEntry';
import { Requests } from './SubSections/Requests/Requests';
import { SubContracts } from './SubSections/SubContracts/SubContracts';
import { TechnologyScan } from './SubSections/TechnologyScan/TechnologyScan';
import TicketCallToAction from './TicketCallToAction';
import { TopicContent } from './TopicContent';
import './topic.sass';

interface Props {
  topic: UserTopicApplicationsQuestionnaires;
  dashboard: Dashboard;
}

export function Topic({ topic, dashboard }: Props) {
  const { t } = useTranslation();
  const { user } = useUser();
  const { questionnaires } = useUserWithCompany();
  const { path } = useRouteMatch();
  const { userValues } = useUserValuesData();
  const {
    navigation: { onboarding },
  } = useNavigation();
  const topicStates = getTopicStates(topic, user, userValues);
  const [collapseOpen, setCollapseOpen] = useState(topicStates.showTeaser);
  const handleToggleCollapse = () => {
    setCollapseOpen(!collapseOpen);
  };

  const TOPIC_LOCKED_TEXT =
    topic.id === 'topic-dsfa'
      ? t('dashboard.topic.forms.formitem.lockedsfa')
      : t('dashboard.topic.forms.formitem.locked');

  // If User has not subscribed and has finished DSE questionnaire, show call to action
  // (only for topic DSE)
  const onboardingQuestionnaireId = topic.applications
    .find((a) => a.id === onboarding.appId)
    ?.questionnaires?.find((q) => q.progress === 1)?.id;
  const onboardingQuestionnaire = questionnaires?.find((q) => q.id === onboardingQuestionnaireId);

  const onboardAppIsPublic = onboardingQuestionnaire?.application.tags?.map((t) => t.tag).includes('public');

  const showTicketCallToAction =
    onboardingQuestionnaire?.application.topic.id === topic.id &&
    !user?.isSubscriber &&
    onboardingQuestionnaireId &&
    !onboardAppIsPublic &&
    topic.id !== Topics.CHECKUP;

  return (
    <div className="dtRouteDashboard__item flex flex-col gap-5">
      <div className="dtRouteDashboard__itemTitle flex flex-row items-center gap-[10px]">
        <Icon type="archive-24" />
        <div className="dtRouteDashboard__itemTitleInput grow">{topic.title}</div>
        <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={t('dashboard.topic.tooltip')}>
          <button onClick={handleToggleCollapse} className="text-gray-400 hover:text-primary-root">
            <HelpOutlineRoundedIcon fontSize="small" />
          </button>
        </Tooltip>
      </div>
      <div className="flex flex-col">
        <LanguageDisclaimer topic={topic} />
        {topic.helpText && topic.helpText?.length > 0 && (
          <Collapse in={collapseOpen}>
            {topic.helpText.map((ht, idx) => (
              <p key={idx}>{ht}</p>
            ))}
          </Collapse>
        )}
      </div>
      {topicStates.isLocked && !showTicketCallToAction && (
        <div className="flex gap-2 items-center">
          <div className="text-primary-root text-3xl">
            <LockIcon fontSize="inherit" />
          </div>
          <span className="italic text-gray-500 text-xs">
            {TOPIC_LOCKED_TEXT} <Link to="/pricing">{t('dashboard.topic.pricinglink')}</Link>
          </span>
        </div>
      )}
      <Switch>
        {/* Routes for subSections */}
        <Route path={`${path}${subSectionUrl(NavigationSubsection.ContractPartners)}`}>
          <PartnerListEntry topic={topic} topicStates={topicStates} />
        </Route>
        <Route path={`${path}${subSectionUrl(NavigationSubsection.TechnologyScan)}`}>
          <TechnologyScan />
        </Route>
        <Route path={`${path}${subSectionUrl(NavigationSubsection.SubContracts)}`}>
          <SubContracts topic={topic} topicStates={topicStates} />
        </Route>
        <Route path={`${path}${subSectionUrl(NavigationSubsection.Incidents)}`}>
          <Incidents topic={topic} topicStates={topicStates} />
        </Route>
        <Route path={`${path}${subSectionUrl(NavigationSubsection.Requests)}`}>
          <Requests topic={topic} topicStates={topicStates} />
        </Route>
        <Route path={`${path}${subSectionUrl(NavigationSubsection.ConsentForms)}`}>
          <ConsentForms topic={topic} topicStates={topicStates} />
        </Route>
        <Route path={`${path}${subSectionUrl(NavigationSubsection.InfoForms)}`}>
          <InfoForms topic={topic} topicStates={topicStates} />
        </Route>
        <Route path={`${path}${subSectionUrl(NavigationSubsection.DSFA)}`}>
          <DSFAList topic={topic} topicStates={topicStates} />
        </Route>
        <Route path={`${path}${subSectionUrl(NavigationSubsection.Usercentrics)}`}>
          <ConsentManagement />
        </Route>

        {showTicketCallToAction && onboardingQuestionnaire ? (
          <Route path={`${path}`}>
            <TicketCallToAction questionnaire={onboardingQuestionnaire} />
          </Route>
        ) : (
          // Regular topic route
          <Route path={`${path}`}>
            <TopicContent
              topic={topic}
              dashboard={dashboard}
              topicStates={topicStates}
              setCollapseOpen={setCollapseOpen}
            />
          </Route>
        )}
      </Switch>
    </div>
  );
}
